import { computed, Injectable, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SelectableItem } from './list-management.types';

/**
 * SelectionManager gestisce la selezione di items con Signals
 * Supporta multi-select, checkbox controls, e selectableMode
 */
@Injectable({ providedIn: 'root' })
export class SelectionManager<T extends SelectableItem> {
  private readonly _selectedItems = signal<(string | number)[]>([]);
  private readonly _checkboxControls = signal<Map<string | number, FormControl>>(new Map());
  private readonly _selectableMode = signal(false);
  private readonly _items = signal<T[]>([]);

  readonly selectedItems$ = computed(() => this._selectedItems());
  readonly checkboxControls$ = computed(() => this._checkboxControls());
  readonly selectableMode$ = computed(() => this._selectableMode());
  readonly items$ = computed(() => this._items());
  readonly areAllSelected$ = computed(() => {
    const itemsList = this._items();
    const selected = this._selectedItems();
    return itemsList.length > 0 && selected.length === itemsList.length;
  });
  readonly hasSelection$ = computed(() => this._selectedItems().length > 0);
  readonly selectionCount$ = computed(() => this._selectedItems().length);

  toggleSelectableMode(): void {
    this._selectableMode.update((v) => !v);
    if (!this._selectableMode()) {
      this.clearSelection();
    }
  }

  toggleItem(itemId: string | number): void {
    const selected = this._selectedItems();
    const index = selected.indexOf(itemId);

    if (index >= 0) {
      selected.splice(index, 1);
    } else {
      selected.push(itemId);
    }

    this._selectedItems.set([...selected]);
    this._updateCheckboxControl(itemId);
  }

  selectAll(): void {
    const itemsList = this._items();
    const allIds = itemsList.map((item) => item.id);
    this._selectedItems.set(allIds);

    itemsList.forEach((item) => {
      this._updateCheckboxControl(item.id, true);
    });
  }

  clearSelection(): void {
    const itemsList = this._items();
    itemsList.forEach((item) => {
      this._updateCheckboxControl(item.id, false);
    });
    this._selectedItems.set([]);
  }

  getSelectedIds(): (string | number)[] {
    return this._selectedItems();
  }

  getSelectionCount(): number {
    return this._selectedItems().length;
  }

  setItems(items: T[]): void {
    this._items.set(items);
  }

  private _updateCheckboxControl(itemId: string | number, checked?: boolean): void {
    const controls = this._checkboxControls();
    let control = controls.get(itemId);

    if (!control) {
      control = new FormControl(false);
      controls.set(itemId, control);
    }

    if (checked !== undefined) {
      control.setValue(checked, { emitEvent: false });
    } else {
      const selected = this._selectedItems();
      const isSelected = selected.includes(itemId);
      control.setValue(isSelected, { emitEvent: false });
    }
  }
}
